import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { DashLangService } from "../../../../../modules/home/dash/service/dash.lang.service";
import { HomeDashCustomize, HomeDashPanelModel } from "../home.dash.panel.model";
import { Item } from "../../../../../pojo/Item";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { EventService } from "../../../../../services/event.service";
import { StateService } from "../../../../../services/state.service";
import { s25Resource } from "../../../../../util/s25.resource";
import { S25ExpressSchedulingComponent } from "../../../../express-scheduling/s25.express.scheduling.component";
import type { ExpressSchedulingPanelApi } from "../../../../express-scheduling/s25.express.scheduling.types";
import { S25IconComponent } from "../../../../../modules/s25-icon/s25.icon.component";

/**
 * Dashboard panel for express scheduling (type 5000) and the reserve-event-only variant (type 5001).
 * Legacy AngularJS used a misleading `data-dash-panel-create-event` name; this matches the primary widget.
 */
@TypeManagerDecorator("s25-ng-dash-panel-express-scheduling")
@Component({
    selector: "s25-ng-dash-panel-express-scheduling",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, S25ExpressSchedulingComponent, S25IconComponent],
    host: { class: "s25-ng-dash-panel-express-scheduling" },
    styles: `
        :host {
            display: block;
        }

        .reserve-event-dash-layout {
            position: relative;
            text-align: center;
        }

        .reserve-event-drag-handle {
            bottom: 0;
            cursor: move;
            left: 0;
            padding: 0;
            position: absolute;
            top: 0;
            width: 2rem;
        }
    `,
    template: `
        @if (createEventLang.value(); as ctx) {
            @if (showPanelBody()) {
                <div
                    [attr.id]="ctx.style?.body"
                    class="sectionBody dash-panel-body dash-panel-body--padded collapse-no-animation"
                    [class.dash-panel-body--center]="showReserveEventForm() && !isToolbox() && !formOnly()"
                >
                    @if (showExpressScheduling()) {
                        <s25-ng-express-scheduling [panelApi]="expressPanelApi" />
                    } @else if (showReserveEventForm()) {
                        <div class="reserve-event-dash-layout">
                            @if (showReserveDragHandle()) {
                                <div
                                    class="sortable-handle reserve-event-drag-handle"
                                    [attr.tabindex]="reserveEventHandleTabIndex()"
                                    [attr.role]="reserveEventKeyboardHandle() ? 'button' : null"
                                    [attr.aria-label]="
                                        reserveEventKeyboardHandle() ? (ctx.text?.button ?? 'Create an Event') : null
                                    "
                                    (focus)="onReserveEventHandleFocus($event)"
                                    (blur)="onReserveEventHandleBlur($event)"
                                ></div>
                            }
                            <button
                                type="button"
                                [attr.id]="ctx.style?.button"
                                (click)="openCreateEventFlow()"
                                tabindex="0"
                            >
                                <s25-ng-icon type="eventsEdit" [ariaLabel]="false" />
                                {{ ctx.text?.button }}
                            </button>
                        </div>
                    }
                </div>
            }
        }
    `,
})
export class HomeDashPanelExpressSchedulingComponent {
    readonly panel = input.required<HomeDashPanelModel>();
    readonly isToolbox = input(false);
    readonly customize = input<HomeDashCustomize | undefined>(undefined);
    /** Logged-in customize keyboard reorder — only used for type 5001 ({@link formOnly}). */
    readonly keyboardSortable = input(false);
    readonly formOnly = input(false);
    readonly refreshTick = input(0);

    /** Populated by {@link S25ExpressSchedulingComponent} for reserve-event handoff. */
    readonly expressPanelApi: ExpressSchedulingPanelApi = {};

    /** Dash lang slice `panel.create_event` (ids/styles for this panel family). */
    readonly createEventLang = s25Resource(async () => {
        const lang = await DashLangService.getLang();
        return lang.panel?.["create_event"] as { style?: Record<string, string>; text?: { button?: string } };
    });

    /** Loaded only when {@link formOnly} is false; matches express scheduling permission gate. */
    readonly expressPerms = s25Resource({
        if: () => !this.formOnly(),
        loader: () => EventService.hasExpressPerms(),
    });

    readonly showExpressScheduling = computed(() => !this.formOnly() && this.expressPerms.value() === true);

    readonly showReserveEventForm = computed(() => this.formOnly() || this.expressPerms.value() === false);

    /** Type 5001 has no panel head on dash; in-body handle replaces the header drag target when reordering. */
    readonly showReserveDragHandle = computed(() => this.formOnly() && this.keyboardSortable());

    /** Same toolbox/collapse gate as other dash panels — header-only when hidden in the toolbox. */
    readonly showPanelBody = computed(() => {
        this.refreshTick();
        return !this.isToolbox() && !this.panel().isCollapsed;
    });

    /** Type 5001 reserve-event keyboard reorder handle (login + form-only); tabindex follows toolbox like headers. */
    readonly reserveEventKeyboardHandle = computed(() => this.formOnly() && this.keyboardSortable());

    /** Tab when toolbox open; click-focusable (`-1`) when closed — same pattern as panel headers. */
    readonly reserveEventHandleTabIndex = computed(() => {
        if (!this.reserveEventKeyboardHandle()) {
            return null;
        }
        return this.customize()?.toolboxOpen() ? 0 : -1;
    });

    onReserveEventHandleFocus(event: FocusEvent): void {
        if (!this.reserveEventKeyboardHandle()) {
            return;
        }
        this.customize()?.onFocus?.(event);
    }

    onReserveEventHandleBlur(event: FocusEvent): void {
        if (!this.reserveEventKeyboardHandle()) {
            return;
        }
        this.customize()?.onFocusOut?.(event);
    }

    async openCreateEventFlow() {
        const expressModel = this.expressPanelApi.getModel?.();
        if (expressModel) {
            await StateService.createEvent(
                {
                    itemName: expressModel.name,
                    itemId: expressModel.location?.itemId,
                    itemTypeId: Item.Ids.Location,
                    startDt: expressModel.startDt,
                    startTime: expressModel.startDt,
                    endDt: expressModel.endDt,
                    endTime: expressModel.endDt,
                },
                Item.Ids.Location,
            );
            this.expressPanelApi.reset?.();
        } else {
            await StateService.createEvent({});
        }
    }
}
